import { type ErrorObject, useVuelidate } from '@vuelidate/core';
import {
    type PropType,
    computed,
    toRef,
    type StyleValue,
    type MaybeRefOrGetter,
} from 'vue';
import _ from 'lodash';
import * as validators from '@vuelidate/validators';

// Field layouts
export type FieldLayout = 'horizontal' | 'vertical' | 'float';
export const fieldLayouts: FieldLayout[] = ['vertical', 'float', 'horizontal'];

// Field error props.
export type FieldErrorProp =
    | string
    | string[]
    | ErrorObject
    | ErrorObject[]
    | boolean
    | null
    | undefined;

export interface FieldProps<TModelValue = any> {
    id?: string;
    name?: string;
    scope?: boolean | string | number | symbol;
    modelValue?: TModelValue;
    class?:
        | string
        | Record<string, boolean>
        | Array<Record<string, boolean> | string>;
    style?: StyleValue;
    inputClass?:
        | string
        | Record<string, boolean>
        | Array<Record<string, boolean> | string>;
    inputStyle?: StyleValue;
    label?: string;
    layout?: FieldLayout;
    for?: string;
    rules?: Record<string, any>;
    maxLength?: number;
    helper?: string;
    errors?: FieldErrorProp;
    valid?: boolean;
    invalid?: boolean;
    loading?: boolean;
    required?: boolean;
    readonly?: boolean;
}

export const fieldPropDefs = {
    id: String,
    name: String,
    modelValue: {},
    class: {
        type: [String, Array, Object] as PropType<
            | string
            | Record<string, boolean>
            | Array<Record<string, boolean> | string>
        >,
        default: null,
    },
    style: {
        type: [String, Object] as PropType<StyleValue>,
        default: undefined,
    },
    inputClass: {
        type: [String, Array, Object] as PropType<
            | string
            | Record<string, boolean>
            | Array<Record<string, boolean> | string>
        >,
        default: null,
    },
    inputStyle: {
        type: [String, Object] as PropType<StyleValue>,
        default: undefined,
    },
    scope: {
        type: null as unknown as PropType<FieldProps['scope']>,
        default: true,
    },
    rules: Object,
    maxLength: Number,
    label: String,
    layout: {
        type: String as PropType<FieldLayout>,
        validator: (v: any) => fieldLayouts.includes(v),
        default: 'vertical',
    },
    for: String,
    helper: String,
    errors: {
        type: [
            String,
            Array,
            Object,
            Boolean,
            null,
        ] as PropType<FieldErrorProp>,
        default: null,
    },
    valid: Boolean,
    invalid: Boolean,
    loading: Boolean,
    required: Boolean,
    readonly: {
        type: Boolean,
        default: false,
    },
};

export interface UseFieldPropsOptions {
    for?: (id: string) => string;
    rules?: Record<string, any>;
    maxLength?: number;
}

export function useFieldProps<P extends FieldProps>(
    props: P,
    emit: { (e: 'update:modelValue', value: any): void },
    options?: MaybeRefOrGetter<UseFieldPropsOptions>
) {
    const opts = toRef(options);
    const id = computed(() => props.id ?? props.name);
    const name = computed(() => props.name ?? props.id);
    const scope = props.scope;
    const modelValue = computed({
        get: () => props.modelValue,
        set: (newValue) => {
            v.value.modelValue.$touch();
            emit('update:modelValue', newValue);
        },
    });

    const maxLength = computed<number | null>(
        () => props.maxLength ?? opts.value?.maxLength ?? null
    );

    const rules = computed(() => {
        const rules = {
            ...(opts.value?.rules ?? {}),
            ...(props.rules ?? {}),
        } as any;
        if (props.required) rules.required = validators.required;
        if (maxLength.value !== null)
            rules.maxLength = validators.maxLength(maxLength.value);
        return { modelValue: rules };
    });

    const v = useVuelidate(
        rules,
        { modelValue },
        {
            $scope: scope,
        }
    );

    const propErrors = computed(() => {
        const errorInput = props.errors;
        if (!errorInput) return [];
        if (typeof errorInput === 'string' && errorInput.trim().length > 0)
            return [errorInput];
        if (!Array.isArray(errorInput)) return [errorInput];
        return errorInput;
    });

    const errors = computed(() => [...propErrors.value, ...v.value.$errors]);

    const forProp = computed<string | undefined>(() => {
        if (props?.for) return props.for;
        if (!id.value) return undefined;
        if (opts.value?.for) return opts.value.for(id.value);
        return id.value;
    });

    const fieldProps = computed<FieldProps>(() => ({
        ..._.pick(props, Object.keys(fieldPropDefs)),
        id: id.value,
        name: name.value,
        for: forProp.value,
        errors: errors.value,
    }));

    return {
        props,
        name,
        id,
        for: forProp,
        scope,
        fieldProps,
        v,
        errors,
        modelValue,
        maxLength,
    };
}
